import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { CalendarIntegrationEvent, CalendarIntegrationProvider } from '../core-services/interfaces/calendar-integration.interface';
import { CalendarAuthService } from './calendar-auth.service';
import { environment } from '../../environments/environment';

export class GoogleAuthService extends CalendarAuthService implements CalendarIntegrationProvider {

  private get accessToken() {
    return this.oauthService.getAccessToken();
  }

  async init() {
    await super.init({
      issuer: 'https://accounts.google.com',
      redirectUri: `${window.location.origin}/dashboard/agenda`,
      clientId: environment.google.calendar.clientId,
      scope: 'openid profile email https://www.googleapis.com/auth/calendar',
      strictDiscoveryDocumentValidation: false,
    });
  }

  async getEvents(start: number, end: number) {
  
    const url = new URL('https://www.googleapis.com/calendar/v3/calendars/primary/events');
    url.searchParams.append('timeMin', new Date(start).toISOString());
    url.searchParams.append('timeMax', new Date(end).toISOString());
    console.log("min", new Date(start).toISOString(), "max", new Date(end).toISOString());
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });
  
    const data = await response.json();
    const events: CalendarIntegrationEvent[] = [];
  
    for (const event of data.items) {
      const normalizedEvent = this.normalize(event);
      const recurrence = this.parseRecurrence(event);
      if (recurrence) {
        const dates = this.normalizeEventDateByRecurrence(normalizedEvent, new Date(start), recurrence);
        if (dates) {
          events.push(...dates.map(date => ({...normalizedEvent, start: date.start.getTime(), end: date.end.getTime()})));
        }
      } else {
        normalizedEvent.end -= 1000;
        events.push(normalizedEvent);
      }
    }

    return events;
  }

  async createEvent(event: CalendarIntegrationEvent) {
    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    });

    if (response.ok) {
      return this.normalize(await response.json());
    }

    throw new Error('Failed to create event');
  }

  async updateEvent(eventId: string, event: any) {
    const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    });

    if (response.ok) {
      return this.normalize(await response.json());
    }

    throw new Error('Failed to update event');
  }

  async deleteEvent(eventId: string) {
    return (await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    })).ok;
  }

  parseRecurrence(event: any) {
    return event.recurrence?.[0]?.split(':')?.[1]?.split(';')?.reduce((acc, cur) => {
      const [key, value] = cur.split('=');
      acc[key] = value;
      return acc;
    }, {});
  }

  // FREQ=DAILY;UNTIL=20230218T025959Z
  // FREQ=WEEKLY;WKST=SU;BYDAY=MO,WE,FR
  normalizeEventDateByRecurrence(event: CalendarIntegrationEvent, date: Date, recurrence: any) {
    const start = new Date(date);
    const end = new Date(date);
    const oldStart = new Date(event.start);
    const oldEnd = new Date(event.end - 1000);
    const result = [];
    switch (recurrence.FREQ) {
      case 'DAILY':
        if (recurrence.UNTIL) {
          const until = new Date(recurrence.UNTIL);
          if (start > until) {
            return null;
          }
        }
        start.setHours(oldStart.getHours(), oldStart.getMinutes(), 0,0);
        end.setHours(oldEnd.getHours(), oldEnd.getMinutes(), 0,0);
        result.push({ start: new Date(start), end: new Date(end) });
        break;
      case 'WEEKLY':
        if (recurrence.UNTIL) {
          const until = new Date(recurrence.UNTIL);
          if (start > until) {
            return null;
          }
        }
        const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
        const calendarDays = recurrence.BYDAY.split(',');
        for (const calendarDay of calendarDays) {
          const day = days.indexOf(calendarDay);
          start.setDate(date.getDate() + (day - date.getDay() + 7) % 7);
          start.setHours(oldStart.getHours(), oldStart.getMinutes(), 0,0);
          end.setDate(start.getDate() + 1);
          end.setHours(oldEnd.getHours(), oldEnd.getMinutes(), 0,0);
          result.push({ start: new Date(start), end: new Date(end) });

        }
        break;
      // case 'MONTHLY':
      //   start.setMonth(date.getMonth() + 1);
      //   end.setDate(start.getDate() + 1);
      //   break;
      // case 'YEARLY':
      //   start.setFullYear(date.getFullYear() + 1);
      //   end.setDate(start.getDate() + 1);
      //   break;
    }
    
    return result;
  }

  normalize(event: any): CalendarIntegrationEvent {

    return {
      id: event.id,
      title: event.summary,
      description: event.description,
      location: event.location,
      start: event.start?.dateTime ? new Date(event.start?.dateTime).getTime() : null,
      end: event.end?.dateTime ? new Date(event.end?.dateTime).getTime() : null,
      allDay: !event.start?.dateTime,
      attendees: event.attendees,
      provider: 'google',
      status: event.status
    }
  }
}
